import { useEffect, useMemo, useState } from 'react';
import { Globe, ExternalLink } from '../../../icons';
import { parseLinkChip } from '../../../lib/link-preview';
import { detectMediaEmbed } from '../../../lib/link-embed';
import { MediaEmbedCard } from './MediaEmbedCard';
import { getLinkPreview, type LinkPreview } from '../../../api/link-preview';

/**
 * LinkPreviewCard (chat-link-preview — 2026-06-30) — "URL만 있는 메시지"를 큰 사이트 미리보기로
 * 렌더. raw URL 텍스트 대신 카드로 대체.
 *
 * 2단계 렌더:
 *   1) 즉시 도메인 카드(파비콘+도메인+경로+URL) 표시 — 서버 응답 전 빈 화면 회피.
 *   2) 서버 OG fetch(getLinkPreview) 가 제목/썸네일을 주면 리치 카드(썸네일+제목+설명+사이트)로
 *      업그레이드. 실패/폐쇄망/메타 없음 → 도메인 카드 유지(폴백).
 *
 * 이모지 없이 lucide SVG. additive — 기존 렌더 경로 무변경.
 */
/**
 * 썸네일이 끝내 없을 때 쓸 도메인 타일 (2026-08-26).
 *
 * 관공서·학교 사이트는 og:image 를 아예 안 주거나 빈 값으로 준다. 서버가 폴백 사다리로
 * 본문 이미지까지 훑어도 못 찾는 곳이 있어(예: 본문이 프레임 안쪽), 그때 카드가 글자만
 * 남으면 "미리보기가 안 나온다" 로 읽힌다. 그림 자리를 도메인 첫 글자로 채워 카드 모양을
 * 지킨다 — 없는 이미지를 지어내지 않으면서 형태만 유지하는 값싼 방법.
 */
function domainTile(host: string): { letter: string; hue: number } {
  const label = host.replace(/^www\./i, '').split('.')[0] || host;
  const letter = (label[0] ?? '?').toUpperCase();
  // 같은 도메인은 늘 같은 색이 되도록 문자열에서 hue 를 만든다(채도·명도는 고정 — 튀지 않게).
  let h = 0;
  for (let i = 0; i < host.length; i += 1) h = (h * 31 + host.charCodeAt(i)) % 360;
  return { letter, hue: h };
}

export function LinkPreviewCard({ url }: { url: string }) {
  const chip = parseLinkChip(url);
  // chat-inline-media-playback (2026-07-12) — 미디어 프로바이더면 임베드 카드로 분기(additive).
  const embed = useMemo(() => detectMediaEmbed(url), [url]);
  // undefined = 로딩, null = 미리보기 없음(폴백), 객체 = 결과
  const [preview, setPreview] = useState<LinkPreview | null | undefined>(undefined);
  const [faviconError, setFaviconError] = useState(false);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    if (embed) return; // 임베드는 OG fetch 불필요.
    let alive = true;
    setPreview(undefined);
    setImageError(false);
    setFaviconError(false);
    void getLinkPreview(url).then((p) => {
      if (alive) setPreview(p);
    });
    return () => {
      alive = false;
    };
  }, [url, embed]);

  // 훅 이후 분기 — hooks 순서 불변(rules-of-hooks 준수).
  if (embed) return <MediaEmbedCard embed={embed} href={url} />;
  if (!chip) return null;

  const showImage = !!preview?.image && !imageError;
  const hasRich = !!preview && (!!preview.title || showImage);

  // ── 리치 카드 (제목/썸네일 확보) ──
  if (hasRich && preview) {
    return (
      <a
        className="chat-link-card chat-link-card--rich"
        href={chip.href}
        target="_blank"
        rel="noopener noreferrer nofollow"
      >
        {showImage && preview.image && (
          <span className="chat-link-card-image">
            <img src={preview.image} alt="" loading="lazy" onError={() => setImageError(true)} />
          </span>
        )}
        {!showImage && (
          // 그림이 없는 사이트 — 도메인 첫 글자로 자리를 채워 카드 형태를 지킨다.
          <span
            className="chat-link-card-tile"
            style={{ background: `hsl(${domainTile(chip.host).hue} 52% 42%)` }}
            aria-hidden
          >
            {domainTile(chip.host).letter}
          </span>
        )}
        <span className="chat-link-card-rich-body">
          {preview.title && <span className="chat-link-card-title">{preview.title}</span>}
          {preview.description && <span className="chat-link-card-desc">{preview.description}</span>}
          <span className="chat-link-card-meta">
            <span className="chat-link-card-meta-fav" aria-hidden>
              {!faviconError ? (
                <img
                  src={chip.faviconUrl}
                  alt=""
                  width={16}
                  height={16}
                  loading="lazy"
                  onError={() => setFaviconError(true)}
                />
              ) : (
                <Globe size={14} />
              )}
            </span>
            <span className="chat-link-card-meta-host">{preview.siteName || chip.host}</span>
          </span>
        </span>
      </a>
    );
  }

  // ── 도메인 카드 (로딩 중 · 미리보기 실패/폐쇄망 폴백) ──
  return (
    <a
      className={`chat-link-card chat-link-card--domain${preview === undefined ? ' is-loading' : ''}`}
      href={chip.href}
      target="_blank"
      rel="noopener noreferrer nofollow"
    >
      <span className="chat-link-card-main">
        <span className="chat-link-card-favicon" aria-hidden>
          {!faviconError ? (
            <img
              src={chip.faviconUrl}
              alt=""
              width={28}
              height={28}
              loading="lazy"
              onError={() => setFaviconError(true)}
            />
          ) : (
            <Globe size={26} />
          )}
        </span>
        <span className="chat-link-card-text">
          <span className="chat-link-card-host">{chip.host}</span>
          {chip.path && <span className="chat-link-card-path">{chip.path}</span>}
        </span>
        <ExternalLink size={16} className="chat-link-card-go" aria-hidden />
      </span>
      <span className="chat-link-card-url">{chip.href}</span>
    </a>
  );
}
